import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';
import { API_CONFIG } from '../config/api.config';

/**
 * Checks the public /app-config endpoint for the `coming_soon_enabled` feature flag.
 * The result is cached (shareReplay) so multiple subscribers only trigger one HTTP call.
 */
@Injectable({ providedIn: 'root' })
export class ComingSoonService {
    private http = inject(HttpClient);

    /** Secret key that bypasses the coming-soon gate (set in localStorage). */
    readonly ACCESS_KEY = 'nibni_internal_access';
    readonly ACCESS_SECRET = 'vnext2026';

    private check$: Observable<boolean> | null = null;

    /**
     * Returns `true` when the coming-soon gate should be shown.
     * Resolves to `false` if:
     *   - The flag is disabled on the server
     *   - The user has the secret localStorage key
     *   - The URL contains `?access=vnext2026`
     *   - The API call fails (fail-open: let users in)
     */
    isComingSoon(): Observable<boolean> {
        // Handle URL-based unlock: ?access=vnext2026
        const urlParams = new URLSearchParams(window.location.search);
        const urlAccess = urlParams.get('access');
        if (urlAccess === this.ACCESS_SECRET) {
            localStorage.setItem(this.ACCESS_KEY, this.ACCESS_SECRET);
            window.history.replaceState({}, '', window.location.pathname);
        }

        // If the secret key is already in localStorage, skip the API call
        if (localStorage.getItem(this.ACCESS_KEY) === this.ACCESS_SECRET) {
            return of(false);
        }

        if (!this.check$) {
            this.check$ = this.http
                .get<any>(`${API_CONFIG.PUBLIC_URL}/app-config`)
                .pipe(
                    map((res) => Boolean(res?.feature_flags?.coming_soon_enabled)),
                    catchError(() => of(false)), // fail-open
                    shareReplay(1)
                );
        }

        return this.check$;
    }
}
